import { AlertCircle, Upload } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import type { Product } from '../../hooks/useProductSystem';
import { useProductSystem } from '../../hooks/useProductSystem';

type CSVProduct = Omit<Product, 'id' | 'position'>;

export function ProductImporter() {
  const { chatbotPublicId } = useChatbot();
  const { importFromCSV, loading, error } = useProductSystem();
  const [dragActive, setDragActive] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseCSV = (content: string): CSVProduct[] => {
    try {
      const lines = content.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Vérifier les colonnes requises
      const requiredColumns = ['name', 'price'];
      const missingColumns = requiredColumns.filter(col => !headers.includes(col));
      if (missingColumns.length > 0) {
        throw new Error(`Colonnes manquantes : ${missingColumns.join(', ')}`);
      }

      const products: CSVProduct[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = line.split(',').map(v => v.trim());
        const product = {} as Partial<CSVProduct>;

        headers.forEach((header, index) => {
          if (header === 'name') {
            product.name = values[index];
          } else if (header === 'price') {
            product.price = parseFloat(values[index]);
          } else if (header === 'description') {
            product.description = values[index];
          } else if (header === 'currency') {
            product.currency = values[index];
          } else if (header === 'image_url') {
            product.image_url = values[index];
          } else if (header === 'category') {
            product.category = values[index];
          } else if (header === 'stock') {
            product.stock = parseInt(values[index], 10);
          } else if (header === 'sku') {
            product.sku = values[index];
          } else if (header === 'tags') {
            product.tags = values[index].split(';').map(t => t.trim());
          } else if (header === 'discount') {
            product.discount = parseFloat(values[index]);
          }
        });

        // Set required fields and defaults
        if (!product.name || product.price === undefined) {
          continue; // Skip invalid products
        }
        
        product.source = 'csv';
        product.currency = product.currency || 'EUR';
        
        products.push(product as CSVProduct);
      }

      return products;
    } catch (err) {
      throw new Error(`Error parsing CSV file : ${err instanceof Error ? err.message : 'Invalid format'}`);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setParseError(null);
    setSuccess(false);

    if (!chatbotPublicId || !e.dataTransfer.files || e.dataTransfer.files.length === 0) return;

    const uploadFile = e.dataTransfer.files[0];
    if (uploadFile.type !== 'text/csv') {
      setParseError('The file must be in CSV format');
      return;
    }

    try {
      const content = await uploadFile.text();
      const products = parseCSV(content);
      
      if (products.length === 0) {
        setParseError('No valid products found in the CSV file');
        return;
      }
      
      await importFromCSV(chatbotPublicId, products);
      setSuccess(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Error while importing');
    }
  }, [chatbotPublicId, importFromCSV]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!chatbotPublicId || !e.target.files || e.target.files.length === 0) return;
    setParseError(null);
    setSuccess(false);

    const uploadFile = e.target.files[0];
    if (uploadFile.type !== 'text/csv') {
      setParseError('The file must be in CSV format');
      return;
    }

    try {
      const content = await uploadFile.text();
      const products = parseCSV(content);
      
      if (products.length === 0) {
        setParseError('No valid products found in the CSV file');
        return;
      }
      
      await importFromCSV(chatbotPublicId, products);
      setSuccess(true);
      e.target.value = '';
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Error while importing');
    }
  }, [chatbotPublicId, importFromCSV]);

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Import of products</h3>

      <div className="space-y-4">
        {(error || parseError) && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-sm">{error || parseError}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 text-green-400 p-4 rounded-lg">
            <p className="text-sm">Import successful! The products have been added.</p>
          </div>
        )}

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 transition-colors ${
            dragActive
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-white/10 hover:border-indigo-500/50'
          }`}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={loading}
            ref={fileInputRef}
          />

          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-400">
              Drag and drop your CSV file here or click to select
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Expected format: name, price, description (optional), category (optional), tags (optional, separated by ;), stock (optional), currency (optional)
            </p>
          </div>

          {loading && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
              <div className="text-white text-sm flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Import in progress...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}